import React from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks } from '../../../components';
import { daysBetween, getStartOf } from '../../../util/dates';

import css from './SearchResultsPanel.module.css';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @param {boolean} [props.showOccasionChips] - Whether to show occasion tags on each
 *   ListingCard (opt-in — see ListingCard.js; used by GiftingPage's grid)
 * @param {string} [props.pageName] - Override the route name PaginationLinks builds hrefs
 *   for (defaults to SearchPage/SearchPageWithListingType). GiftingPage passes its own
 *   route name so pagination stays on /gifts or /occasions/:slug instead of routing to /s.
 * @param {Object} [props.pagePathParams] - Path params for the pageName route override
 *   above (defaults to { listingType: listingTypeParam }).
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    showOccasionChips = false,
    pageName: pageNameOverride,
    pagePathParams: pagePathParamsOverride,
    intl,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const pageName =
    pageNameOverride || (listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage');
  const pagePathParams = pagePathParamsOverride || { listingType: listingTypeParam };

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={pagePathParams}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  return (
    <div className={classes}>
      <ul className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {listings.map(l => {
          // Calculate badge data
          const { createdAt, currentStock, publicData } = l.attributes;
          const isBestseller = publicData?.isBestseller || false;
          const stockCount = currentStock?.quantity || null;

          // Check if listing is new (created within last 7 days)
          const now = new Date();
          const listingCreatedDate = createdAt ? new Date(createdAt) : null;
          const isNew = listingCreatedDate
            ? daysBetween(listingCreatedDate, now) <= 7
            : false;

          return (
            <li key={l.id.uuid} className={css.resultItem}>
              <ListingCard
                className={css.listingCard}
                listing={l}
                renderSizes={cardRenderSizes(isMapVariant)}
                setActiveListing={setActiveListing}
                showTrustBadges={true}
                showConversionBadges={true}
                isBestseller={isBestseller}
                stockCount={stockCount}
                isNew={isNew}
                showOccasionChips={showOccasionChips}
              />
            </li>
          );
        })}
        {props.children}
      </ul>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
